import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage from '../../components/LegalPage';
import legalConfig from '../../lib/legalConfig';
import { useLanguage } from '../../context/LanguageContext';

const META_DESC =
  'CosmoEstates Disclaimer: the AI Property Price Estimate, Renovation cost tool, Neighbourhood Intelligence, and Mortgage Calculator are indicative tools, not valuations, surveys, or financial advice.';

export default function DisclaimerPage() {
  const { t } = useLanguage();
  return (
    <LegalPage
      title={t('legal.docs.disclaimerTitle')}
      metaTitle="Disclaimer | CosmoEstates"
      metaDescription={META_DESC}
    >
      <div className="legal-meta">
        <p>
          <strong>CosmoEstates</strong> — {legalConfig.entityName}
        </p>
        <p>
          <strong>Website:</strong>{' '}
          <a href={legalConfig.websiteUrl}>{legalConfig.websiteUrl}</a>
        </p>
      </div>

      <p className="legal-draft-note">
        Draft for review by Cyprus-qualified counsel. Not legal advice.
      </p>

      <h2>1. General</h2>
      <p>
        The information and tools on CosmoEstates are provided for{' '}
        <strong>general information purposes only</strong>. While we try to keep things useful
        and up to date, we make no representation or warranty, express or implied, about the
        accuracy, completeness, reliability, or suitability of any information, listing, or tool
        output on the Platform. Any reliance you place on it is{' '}
        <strong>strictly at your own risk</strong>.
      </p>
      <p>
        CosmoEstates is a marketplace and information platform.{' '}
        <strong>
          We are not an estate agent, valuer, surveyor, contractor, mortgage broker, financial
          adviser, tax adviser, or legal adviser
        </strong>
        , and nothing on the Platform is professional advice. Always obtain independent
        professional advice before making a property, financial, or renovation decision.
      </p>

      <h2>2. Listings are posted by users</h2>
      <p>
        Property listings, prices, descriptions, and images are submitted by users and third
        parties. We do not own, inspect, verify, or guarantee them. We are not responsible for
        inaccurate, outdated, misleading, or fraudulent listings, and the existence of a listing
        on the Platform is not an endorsement. Verify any property and its details independently
        before acting.
      </p>

      <h2>3. Property Price Estimate</h2>
      <p>
        The Property Price Estimate is an{' '}
        <strong>automated, AI-generated, indicative figure</strong> based on the information you
        enter (such as type, location, and size).{' '}
        <strong>
          It is NOT a valuation, appraisal, or professional opinion of value
        </strong>
        , and we do not claim accuracy.
      </p>
      <p>
        The Cyprus property market is highly variable — for example a standard home may sit next
        to a large premium development, causing the average price per square metre in an area to
        fluctuate significantly. The estimate cannot capture the specific condition, features,
        legal status, or circumstances of a property.{' '}
        <strong>
          Do not rely on it for buying, selling, lending, insurance, tax, or any financial
          decision.
        </strong>{' '}
        For an accurate value, instruct a qualified, independent valuer.
      </p>

      <h2>4. Renovation cost tool</h2>
      <p>
        The renovation tool gives an{' '}
        <strong>indicative, automated estimate</strong> of possible renovation costs based on
        the room and changes you enter. Actual costs depend on materials, labour, site
        conditions, scope, and market prices, and will differ. <strong>It is not a quote.</strong>{' '}
        Always obtain firm quotations from qualified contractors before committing to any work.
      </p>

      <h2>5. Find Technician / Find Agent introductions</h2>
      <p>
        These features collect your details and connect you with third-party contractors,
        technicians, or agents ("Providers"). We do not employ, supervise, endorse, vet beyond
        any stated checks, or guarantee any Provider, their work, pricing, qualifications,
        licensing, or conduct. Any engagement is solely between you and the Provider. See the{' '}
        <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link> for how
        your details are shared.
      </p>

      <h2>6. Neighbourhood Intelligence</h2>
      <p>
        Neighbourhood Intelligence provides scores and statistics (such as transport, schools,
        amenities, expat community, and property price indices) generated through automated/AI
        methods using information sourced from public data. It is provided{' '}
        <strong>for general information only</strong>, may be incomplete, out of date, or
        inaccurate, and is <strong>not</strong> professional, investment, or location advice.
        Verify anything important with official and independent sources.
      </p>

      <h2>7. Financial Compass / mortgage calculator</h2>
      <p>
        The Financial Compass is a{' '}
        <strong>calculator for illustration only</strong>. Outputs (including
        principal/interest breakdowns, yearly graphs, and overpayment savings) are based on the
        figures and interest rate <strong>you</strong> input.{' '}
        <strong>
          It is not a mortgage offer, financial advice, or a guarantee of any rate, approval, or
          outcome.
        </strong>{' '}
        Interest rates change — check current rates with lenders. Speak to a qualified mortgage
        or financial adviser and your lender before making borrowing decisions.
      </p>

      <h2>8. Third-party content and links</h2>
      <p>
        The Platform may link to or display third-party websites, data, and services. We are not
        responsible for their content, accuracy, or availability, and a link is not an
        endorsement.
      </p>

      <h2>9. No liability</h2>
      <p>
        To the maximum extent permitted by law, and subject to the rights you have as a consumer
        under Cyprus law that cannot be excluded, CosmoEstates accepts no liability for any loss
        or damage arising from your use of, or reliance on, any information, listing, tool,
        estimate, or introduction on the Platform. Your use is governed by our{' '}
        <Link to="/terms">Terms &amp; Conditions</Link>, including the limitation of liability
        set out there.
      </p>
    </LegalPage>
  );
}
